import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AppConfigurationService from '@services/app-configuration-service/app-configuration-service';
import { BetaWrapper } from '@utils/beta-wrapper/beta-wrapper';
import { useAppInformationContent } from './hook/use-breadcrumb-content';
import './breadcrumbs.scss';
import { SYNDICATED_HEADER_COUNTRIES } from '@/constants';
import { findPathByAlias } from '@/routes/routesList';
import { useWindowSize } from '@/hooks/use-window-size';
import RightArrowIcon from '@views/order-details-view/components/cost/tax-tooltip/icons/right-arrow-icon';
import Separator from '@sections/breadcrumbs/separator/breadcrumb-separator';

interface Props {
    currentPage: string;
    hideBaseBreadcrumb?: boolean;
    previousPage?: string;
    previousLink?: string;
    secondPreviousPage?: string;
    secondPreviousLink?: string;
    thirdPreviousPage?: string;
    thirdPreviousLink?: string;
    fourthPreviousPage?: string;
    fourthPreviousLink?: string;
    fromSignIn?: boolean;
}

export const Breadcrumbs = (props: Props) => {
    const appInformationContent = useAppInformationContent();
    const isLandingPage = props.currentPage === 'Landing';
    const { currentRegionCode, brand } = new AppConfigurationService();
    const size = useWindowSize();
    const [isMobile, setIsMobile] = useState<boolean>(size.width < 768);
    const [showFullBreadcrumb, setShowFullBreadcrumb] = useState<boolean>(true);
    const isSyndicatedHeader =
        SYNDICATED_HEADER_COUNTRIES.includes(currentRegionCode);
    const isLincoln = brand === 'lincoln';
    const crumbSeparator =
        isLincoln || props.fromSignIn ? (
            <RightArrowIcon color="#000000" />
        ) : (
            '/'
        );

    const HomepageBreadcrumb = () => {
        if (appInformationContent) {
            return (
                <BetaWrapper
                    text={appInformationContent.breadcrumbStartingValue}
                />
            );
        } else {
            return <></>;
        }
    };

    useEffect(() => {
        if (size.width < 768 && !isMobile) {
            setIsMobile(true);
            if (!props.hideBaseBreadcrumb) {
                setShowFullBreadcrumb(false);
            }
        } else if (size.width >= 768 && isMobile) {
            setIsMobile(false);
            setShowFullBreadcrumb(true);
        }
    }, [size.width]);

    const DynamicBreadcrumbs = () => {
        return appInformationContent && size.width ? (
            <>
                {!props.hideBaseBreadcrumb ? (
                    <>
                        <Link
                            to={findPathByAlias('GlobalAccountHomePage')}
                            className="breadcrumb-link"
                        >
                            <BetaWrapper
                                text={
                                    appInformationContent.breadcrumbStartingValue
                                }
                            />
                        </Link>
                        {props.fromSignIn ? (
                            <Separator brand={brand} />
                        ) : (
                            <span className="back-slash">{crumbSeparator}</span>
                        )}
                    </>
                ) : null}
                {props.previousPage && isMobile && !showFullBreadcrumb && (
                    <>
                        <button
                            className="breadcrumb-link"
                            aria-label="Show full breadcrumb navigation"
                            aria-expanded="false"
                            onClick={() => {
                                setShowFullBreadcrumb(true);
                            }}
                        >
                            <BetaWrapper text={'...'} />
                        </button>
                        <span className="back-slash">{crumbSeparator}</span>
                    </>
                )}
                {showFullBreadcrumb && (
                    <>
                        {props.fourthPreviousPage && (
                            <>
                                <Link
                                    to={props.fourthPreviousLink}
                                    className="breadcrumb-link"
                                >
                                    <BetaWrapper
                                        text={props.fourthPreviousPage}
                                    />
                                </Link>
                                <span className="back-slash">
                                    {crumbSeparator}
                                </span>
                            </>
                        )}
                        {props.thirdPreviousPage && (
                            <>
                                <Link
                                    to={props.thirdPreviousLink}
                                    className="breadcrumb-link"
                                >
                                    <BetaWrapper
                                        text={props.thirdPreviousPage}
                                    />
                                </Link>
                                <span className="back-slash">
                                    {crumbSeparator}
                                </span>
                            </>
                        )}
                        {props.secondPreviousPage && (
                            <>
                                <Link
                                    to={props.secondPreviousLink}
                                    className="breadcrumb-link"
                                >
                                    <BetaWrapper
                                        text={props.secondPreviousPage}
                                    />
                                </Link>
                                <span className="back-slash">
                                    {crumbSeparator}
                                </span>
                            </>
                        )}
                        {props.previousPage && (
                            <>
                                <Link
                                    to={props.previousLink}
                                    className="breadcrumb-link"
                                >
                                    <BetaWrapper text={props.previousPage} />
                                </Link>
                                <span className="back-slash">
                                    {crumbSeparator}
                                </span>
                            </>
                        )}
                    </>
                )}
                <span className="current-page">{props.currentPage}</span>
            </>
        ) : (
            <></>
        );
    };

    return (
        <div
            className={`breadcrumbs ${
                isSyndicatedHeader ? 'syndicated-header' : ''
            }`}
            data-testid="breadcrumbs-component"
        >
            <div className="breadcrumbs-content">
                {isLandingPage ? (
                    <HomepageBreadcrumb />
                ) : (
                    <DynamicBreadcrumbs />
                )}
            </div>
        </div>
    );
};
